import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useReducer,
} from 'react';

import {nanoid} from 'nanoid';

import {useForm, FormType, FormValues} from '@netx/core-components/Form';

import actions, {ActionType} from './store/actions';
import reducer, {initialState} from './store/reducers';

export type ItemManagerStoreContextType = {
	state: typeof initialState;
	getItems(): any[];
	addItem(values: FormValues): void;
	addItems(items: FormValues[]): void;
	removeItem(id: number | string): void;
	resetItems(items?: FormValues[]): void;
	form: FormType;
};

const ActionsContext = createContext<ActionType>({} as any);
const ItemsManagerServiceContext = createContext<ItemManagerStoreContextType>(
	{} as any,
);

export const useItemManagerActions = () =>
	useContext<ReturnType<typeof actions>>(ActionsContext);
export const useItemManagerStore = () =>
	useContext<ItemManagerStoreContextType>(ItemsManagerServiceContext);

const getNewItemValues = (formValues: FormValues, id: number | string) => {
	const values: FormValues = {};
	Object.entries(formValues).forEach(([key, value]) => {
		values[`${key}[${id}]`] = value;
	});
	return values;
};

const getNewItemsValues = (
	items: FormValues[],
	ids: Array<number | string>,
) => {
	return items
		.map((item, idx) => getNewItemValues(item, ids[idx]))
		.reduce((result, currentObject) => {
			for (const key in currentObject) {
				if (currentObject.hasOwnProperty(key)) {
					result[key] = currentObject[key];
				}
			}
			return result;
		}, {});
};

export const ItemManagerServiceProvider = ({
	children,
}: React.PropsWithChildren<{}>) => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const dispatchActions = useMemo(() => actions(dispatch), [dispatch]);

	const form = useForm(undefined, {
		alwaysValidate: true,
		removeValueOnUnregister: false,
	});

	const addItem = useCallback(
		(values: any) => {
			const id = dispatchActions.add();
			form.setValues((formValues) => ({
				...formValues,
				...getNewItemValues(values, id),
			}));
		},
		[dispatchActions.add, form.setValues],
	);

	const addItems = useCallback(
		(items: FormValues[]) => {
			const ids = items.map(() => nanoid());
			dispatchActions.addMultiple(ids);
			form.setValues((formValues) => ({
				...formValues,
				...getNewItemsValues(items, ids),
			}));
		},
		[addItem],
	);

	const removeItem = useCallback((id: number | string) => {
		dispatchActions.remove(id);
		form.setValues((formValues) => {
			const newValues = {...formValues};
			delete newValues[`value[${id}]`];
			return newValues;
		});
	}, []);

	const resetItems = useCallback(
		(items: FormValues[] = []) => {
			const ids = items.map(() => nanoid());
			dispatchActions.reset(ids);
			form.setValues((formValues) => ({
				...formValues,
				...getNewItemsValues(items, ids),
			}));
		},
		[dispatchActions.reset],
	);

	const getItems = useCallback(() => {
		const values: FormValues = {};
		const orderedValues: FormValues[] = [];

		// First collect the values all under their respective uuid
		const formValues = form.valuesRef.current;
		for (const key in formValues) {
			const pieces = key.match(/(.*)\[(.*)\]/);
			if (!pieces) continue;

			const [, fieldName, uuid] = pieces;
			if (!values[uuid]) values[uuid] = {};
			values[uuid][fieldName] = formValues[key];
		}

		// Then put values, in order, into an array (without the uuid)
		const {items} = state;
		for (const key in values) {
			const idx = items.indexOf(key);
			if (idx < 0) continue;
			orderedValues[idx] = values[key];
		}

		return orderedValues;
	}, [state.items]);

	return (
		<ItemsManagerServiceContext.Provider
			value={{state, addItem, addItems, getItems, removeItem, resetItems, form}}
		>
			<ActionsContext.Provider value={dispatchActions}>
				{children}
			</ActionsContext.Provider>
		</ItemsManagerServiceContext.Provider>
	);
};
